import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { EditableSelect } from '@/components/EditableSelect';
import { Field, Input, Select, Textarea } from '@/components/Form';
import { productionPlansApi, type ProductionPlan } from '@/api/production-plans';
import { extractApiError } from '@/api/client';
import { modelsApi } from '@/api/models';
import { useToast } from '@/components/Toast';
// 추가수정 2차(26-07-27) 정정 — 날짜 기준은 KST 정본으로.
import { kstYmd } from '@/lib/format-date';

// round5 #10 — 제조 계획 수정 모달. PATCH API 는 있었으나 UI 호출처가 0건이라
// '방금 잘못 등록한 계획' 도 취소 후 재등록해야 했다.
//
// 2-tier 정책(서버 가드와 동일):
//   - 이력(활성 할당·완성 로봇) 없음 → 모델·사양 3축 포함 전 필드 수정.
//   - 이력 있음 → 수량(하한 = 할당+완성)·ETA·우선순위·batch·메모만.
//     사양 필드는 잠금 + 사유 안내 (영업이 reserve 한 slot 의 spec 이
//     슬그머니 바뀌는 사고 차단).
// humanId 는 재생성하지 않는다(외부 참조 안정).

interface Props {
  open: boolean;
  onClose: () => void;
  plan: ProductionPlan;
}

export function ProductionPlanEditModal({ open, onClose, plan }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const allocated = plan.allocatedCount ?? 0;
  const materialized = plan.materializedCount ?? 0;
  const hasHistory = allocated > 0 || materialized > 0;
  const minCount = Math.max(1, allocated + materialized);

  const [modelId, setModelId] = useState(plan.modelId);
  const [plannedCountStr, setPlannedCountStr] = useState(String(plan.plannedCount));
  const [priorityStr, setPriorityStr] = useState(String(plan.priority ?? 0));
  // 추가수정 2차(26-07-27) 정정 — 목록·대시보드가 KST 로 그리는 값이므로
  // 수정창 기본값도 같은 기준으로 뽑는다(보이는 날짜와 다른 날짜가 채워져
  // 있으면 저장만 눌러도 예정일이 바뀐다). date input 규격이라 하이픈 유지.
  const [estimatedCompletionAt, setEstimatedCompletionAt] = useState(
    kstYmd(plan.estimatedCompletionAt)?.replace(/\./g, '-') ?? '',
  );
  const [batchCode, setBatchCode] = useState(plan.batchCode ?? '');
  const [notes, setNotes] = useState(plan.notes ?? '');
  const [color, setColor] = useState(plan.color ?? '');
  const [sensorType, setSensorType] = useState(plan.sensorType ?? '');
  const [variantValue, setVariantValue] = useState(plan.variantValue ?? '');

  const plannedCount = Math.max(0, Math.floor(Number(plannedCountStr) || 0));
  const priority = Math.max(0, Math.floor(Number(priorityStr) || 0));

  const modelsQ = useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    enabled: open && !hasHistory,
  });
  const selectedModel = modelsQ.data?.items.find((m) => m.id === modelId);

  const update = useMutation({
    mutationFn: (payload: Partial<ProductionPlan>) => productionPlansApi.update(plan.id, payload),
    onSuccess: () => {
      toast.success(`${plan.humanId} 제조 계획을 수정했습니다.`);
      qc.invalidateQueries({ queryKey: ['production-plans'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      onClose();
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (plannedCount < minCount) {
      toast.error(
        hasHistory
          ? `수량은 할당 ${allocated}대와 완성 ${materialized}대를 더한 ${minCount}대 이상이어야 합니다.`
          : '수량은 1 이상이어야 합니다.',
      );
      return;
    }
    const isoEta = new Date(`${estimatedCompletionAt}T09:00:00.000Z`).toISOString();
    update.mutate({
      plannedCount,
      estimatedCompletionAt: isoEta,
      priority,
      batchCode,
      notes,
      // 이력 없는 계획만 사양 전송 — 서버 2-tier 가드와 짝. '' 는 클리어.
      ...(!hasHistory
        ? {
            ...(modelId !== plan.modelId ? { modelId } : {}),
            color,
            sensorType,
            variantValue: variantValue.trim(),
          }
        : {}),
    });
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!update.isPending) onClose();
      }}
      title={`${plan.humanId} 제조 계획 수정`}
      width={520}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={update.isPending} style={btn}>
            취소
          </button>
          <button
            type="submit"
            form="production-plan-edit-form"
            disabled={update.isPending}
            style={{
              ...btn,
              background: 'var(--blue-600)',
              color: 'var(--fg-inverse)',
              fontWeight: 600,
            }}
          >
            {update.isPending ? '저장 중…' : '저장'}
          </button>
        </div>
      }
    >
      <form
        id="production-plan-edit-form"
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {hasHistory && (
          <div
            data-testid="plan-edit-history-notice"
            style={{
              fontSize: 12,
              padding: '8px 10px',
              borderRadius: 'var(--r-btn)',
              background: 'var(--bg-warning-subtle, var(--bg-accent-subtle))',
              color: 'var(--fg-muted)',
              lineHeight: 1.5,
            }}
          >
            할당 {allocated}대와 완성 {materialized}대 이력이 있어 모델과 사양은 바꿀 수 없습니다.
            사양을 바꾸려면 할당을 해제하거나 새 계획을 등록해 주세요.
          </div>
        )}

        {hasHistory ? (
          <Field label="모델 (잠금)" htmlFor="ppe-model-locked">
            <Input
              id="ppe-model-locked"
              value={plan.model?.displayName ?? plan.modelId}
              disabled
              readOnly
            />
          </Field>
        ) : (
          <Field label="모델" required htmlFor="ppe-model">
            <Select
              id="ppe-model"
              value={modelId}
              onChange={(e) => {
                setModelId(e.target.value);
                // 모델 변경 시 이전 모델의 구분 사양 값이 남지 않도록 리셋(축이 다름).
                setVariantValue('');
              }}
              required
            >
              {modelsQ.data?.items?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName} ({m.code})
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field
            label="계획 수량"
            required
            htmlFor="ppe-count"
            hint={hasHistory ? `할당과 완성을 더한 ${minCount}대 이상이어야 합니다.` : undefined}
          >
            <Input
              id="ppe-count"
              type="number"
              min={minCount}
              value={plannedCountStr}
              onChange={(e) => setPlannedCountStr(e.target.value)}
              onBlur={() =>
                setPlannedCountStr((s) =>
                  String(Math.max(minCount, Math.floor(Number(s) || minCount))),
                )
              }
              required
            />
          </Field>
          <Field label="우선순위" htmlFor="ppe-priority">
            <Input
              id="ppe-priority"
              type="number"
              min={0}
              value={priorityStr}
              onChange={(e) => setPriorityStr(e.target.value)}
              onBlur={() => setPriorityStr((s) => String(Math.max(0, Math.floor(Number(s) || 0))))}
            />
          </Field>
        </div>

        <Field label="완성 예정일 (ETA)" required htmlFor="ppe-eta">
          <Input
            id="ppe-eta"
            type="date"
            value={estimatedCompletionAt}
            onChange={(e) => setEstimatedCompletionAt(e.target.value)}
            required
          />
        </Field>

        {!hasHistory && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="색상" hint="비워 둬도 됩니다." htmlFor="ppe-color">
                <EditableSelect
                  id="ppe-color"
                  groupKey="robot.color"
                  value={color}
                  onChange={setColor}
                  placeholder="— 선택 —"
                />
              </Field>
              <Field label="불감지" hint="F 또는 N. 비워 둬도 됩니다." htmlFor="ppe-sensor">
                <EditableSelect
                  id="ppe-sensor"
                  groupKey="robot.sensorType"
                  value={sensorType}
                  onChange={setSensorType}
                  placeholder="— 선택 —"
                />
              </Field>
            </div>
            {selectedModel?.variantOptionGroupKey && (
              <Field
                label={selectedModel.variantAxisLabel ?? '구분 사양'}
                hint="목록에 없으면 매니저가 '옵션 추가/삭제…'에서 값을 더할 수 있습니다."
                htmlFor="ppe-variant"
              >
                <EditableSelect
                  id="ppe-variant"
                  groupKey={selectedModel.variantOptionGroupKey}
                  value={variantValue}
                  onChange={setVariantValue}
                  placeholder="— 선택 —"
                />
              </Field>
            )}
          </>
        )}

        <Field label="배치 코드 (선택)" hint="예: 2026Q2-B01" htmlFor="ppe-batch">
          <Input
            id="ppe-batch"
            type="text"
            value={batchCode}
            onChange={(e) => setBatchCode(e.target.value)}
            placeholder="2026Q2-B01"
          />
        </Field>

        <Field label="메모" htmlFor="ppe-notes">
          <Textarea
            id="ppe-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="추가 정보"
          />
        </Field>
      </form>
    </Modal>
  );
}

const btn: React.CSSProperties = {
  padding: '8px 16px',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  borderRadius: 'var(--r-btn)',
  cursor: 'pointer',
  fontSize: 13,
};
